import { get } from "lodash-es"

import recallTextJson from "./recall-zh-cn.json"
import textJSON from "./zh-cn.json"

type Primitive = string | number | boolean | null
type JSONValue = Primitive | JSONObject | JSONArray
type JSONObject = { [key: string]: JSONValue }
type JSONArray = JSONValue[]

/**
 * 获取所有指向基本类型（Primitive）的属性路径
 */
type PrimitivePaths<T, ParentPath extends string = ""> = T extends JSONObject
    ? {
          [K in keyof T]-?: K extends string
              ? T[K] extends JSONObject
                  ? PrimitivePaths<T[K], JoinPath<ParentPath, K>>
                  : JoinPath<ParentPath, K>
              : never
      }[keyof T]
    : never

/**
 * 辅助类型：拼接路径（处理空父路径）
 */
type JoinPath<Parent extends string, Child extends string> = Parent extends ""
    ? Child
    : `${Parent}.${Child}`

export type I18nTextPath = PrimitivePaths<typeof textJSON>

export type RecallTextPath = PrimitivePaths<typeof recallTextJson>

export const i18n = {
    get(
        path: I18nTextPath,
        params: Record<string, string | number> = {},
    ): string {
        const raw = get(textJSON, path, "")
        // 确保返回的是字符串类型，如果是数组则返回空字符串
        if (Array.isArray(raw) || typeof raw !== "string") {
            return ""
        }
        let result = raw
        for (const key in params) {
            if (key in params) {
                const value = params[key]
                result = result.replace(`{{${key}}}`, String(value))
            }
        }
        return result
    },
    getRecall(
        path: RecallTextPath,
        params: Record<string, string | number> = {},
    ): string {
        const raw = get(recallTextJson, path, "")
        // 确保返回的是字符串类型，如果是数组则返回空字符串
        if (Array.isArray(raw) || typeof raw !== "string") {
            return ""
        }
        let result = raw
        for (const key in params) {
            if (key in params) {
                const value = params[key]
                result = result.replace(`{{${key}}}`, String(value))
            }
        }
        return result
    },
    /**
     * 获取数组类型的 i18n 值（规则列表等）
     * 使用对象结构存储数组，通过数字键访问，如 rule.items.0, rule.items.1
     */
    getArray<T = string>(path: string, type: "main" | "recall" = "main"): T[] {
        const source = type === "main" ? textJSON : recallTextJson
        const raw = get(source, path)

        if (!raw || typeof raw !== "object") {
            return []
        }

        // 如果是真正的数组，直接返回
        if (Array.isArray(raw)) {
            return raw as T[]
        }

        // 如果是对象结构模拟的数组（通过数字键），转换为数组
        const keys = Object.keys(raw)
            .map(Number)
            .filter((k) => !Number.isNaN(k))
            .sort((a, b) => a - b)

        return keys.map((key) => (raw as Record<string, T>)[String(key)])
    },
}
